import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonIcon, ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import { FormFieldPresentationProps } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import buttonDisplayProps from "@insite/mobius/Pagination/buttonDisplayProps";
import Select, { SelectComponentProps } from "@insite/mobius/Select";
import Typography from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import { setCookie } from "@insite/mobius/utilities/cookies";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import {
    appendLinkElement,
    getCanonical,
    getPrevCurrentNextQueryString,
    hasLinksWithRelPrevNext,
    removeLinkElement,
    updateCanonical,
} from "@insite/mobius/utilities/linkElementsManipulation";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { COLORS, NISCO, TYPOGRAPHY } from "@tCentro/theme/design";
import * as React from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";

export interface PaginationPresentationProps {
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        currentButton?: StyledProp<PaginationProps>;
        ellipsis?: StyledProp<PaginationProps>;
        linkList?: StyledProp<PaginationProps>;
        pagination?: StyledProp<PaginationProps>;
        perPageSelect?: StyledProp<PaginationProps>;
    };
    /** An object containing props to be passed down to the button component inside pagination.
     * @themable */
    buttonProps?: ButtonPresentationProps;
    /** The button variant that should be used for the current button.
     * @themable */
    currentPageButtonVariant?: ButtonVariants;
    /** An object containing props to be passed down to the select component inside pagination
     * @themable */
    selectProps?: FormFieldPresentationProps<SelectComponentProps>;
    /** An object containing icon sources for the navigation button icons.
     * @themable */
    navIconsSrc?: {
        firstPage?: React.ComponentType | string;
        previousPage?: React.ComponentType | string;
        nextPage?: React.ComponentType | string;
        lastPage?: React.ComponentType | string;
    };
}

export interface NISProductListPaginationStyles {
    container?: InjectableCss;
    paginatinLinkStyles?: InjectableCss;
}

export const nisPaginationStyles: NISProductListPaginationStyles = {
    container: {
        css: css`
            margin-top: 34px;
        `,
    },
    paginatinLinkStyles: {
        css: css`
            cursor: pointer;
            font-family: inherit;
            min-height: 0;
            line-height: 1;
            backface-visibility: hidden;
            transition: all 200ms ease-in-out 0s;
            word-break: keep-all;
            border-radius: 80px !important;
            background: #d5dcea;
            margin: 0px 8px 0px 0px !important;
            padding: 4px !important;
            border: none;
            height: 32px;
            width: 32px;
            justify-content: center;
            > span {
                color: #333333 !important;
                font-weight: 600 !important;
                font-family: Roboto Condensed;
                font-size: 18px !important;
                line-height: 24px !important;
            }
            &[aria-label^="Current"] {
                background: ${getColor("primary")};
                min-height: 0px;
                height: 32px;
                width: 32px;
                padding: 4px !important;
                margin: 0px 8px 0px 0px !important;
                border-radius: 80px !important;
                &:hover {
                    cursor: default;
                    color: white;
                    filter: brightness(150%);
                }
                > span {
                    color: white !important;
                    font-size: 18px;
                    line-height: 24px;
                    font-weight: 600;
                }
            }
        `,
    },
};

const styles = nisPaginationStyles;

export type PaginationComponentProps = MobiusStyledComponentProps<
    "div",
    {
        pageSizeCookie?: string;
        /** The index of the current page of results being displayed */
        currentPage: number;
        /** String describing the "results per page" select */
        resultsPerPageLabel?: string;
        /** OnChange function passed to the select component governing the number of results per page. Receives the event as an argument. */
        onChangeResultsPerPage: (event: React.ChangeEvent<HTMLSelectElement>, resultsPerPage: number) => void;
        /** Function to build href passed to each button in the pagination button/link list. Receives the page index as an argument. */
        createHref?: (page: number) => void;
        /** OnChange function passed to each button in the pagination button/link list. Receives the page index as an argument. */
        onChangePage: (page: number) => void;
        /** Number of results currently being displayed on each page */
        resultsPerPage: number;
        /** Number of total results to be displayed within the pagination component */
        resultsCount: number;
        /** Options to be displayed under the "results per page" select */
        resultsPerPageOptions: number[];
        /** String describing the URL of the previous page in the series */
        prevPage?: string;
        /** String describing the the URL of the next page in the series */
        nextPage?: string;
    }
>;

export type PaginationProps = PaginationPresentationProps & PaginationComponentProps & ThemeProps<BaseTheme>;

const responsiveStyles = css`
    margin: 4px 0;
    width: 100%;
    justify-content: center;
    display: flex;
    align-items: center;
`;

const iconButtonStyles = css`
    &:disabled {
        span {
            top: 0px !important;
            > svg {
                color: #939393 !important;
                stroke: #939393 !important;
            }
        }
    }
    span {
        top: 0px !important;
        > svg {
            color: #333333 !important;
            stroke: #333333 !important;
        }
    }
    width: 24px !important;
    height: 24px !important;
    margin: 0px !important;
    min-height: 0px !important;
    justify-content: center;
`;

const numericButtonStyles = css`
    padding: 0 8px;
`;

const PaginationStyle = styled.div<InjectableCss>`
    display: flex;
    align-items: center;
    background: rgb(255, 255, 255);
    margin: 0px !important;
    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    flex-direction: column-reverse;
                `,
                css``,
                css``,
                null,
                null,
            ],
            "max",
        )};
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    ${injectCss}
`;

const LinkList = styled.nav<InjectableCss>`
    display: flex;
    justify-content: flex-end;
    flex-basis: 50%;
    flex-direction: column;
    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    justify-content: center;
                    flex-basis: auto;
                    padding-right: 0px;
                `,
                null,
                null,
                null,
                null,
            ],
            "max",
        )};

    ul {
        list-style: none;
        justify-content: center;
        display: flex;
        align-items: center;
    }
    li {
        display: inline-block;
        button[aria-label^="Current"] {
            background: ${NISCO.COLORS.primaryColor};
            padding: 4px !important;
            border-radius: 80px !important;
            min-height: 0;
            height: 32px;
            width: 32px;
            margin: 0px 8px 0px 0px !important;
            span {
                font-size: ${TYPOGRAPHY.size.large};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                color: #ffffff !important;
            }
        }
    }
    li[data-test-selector="itemCountLine"] {
        font-family: Roboto;
        font-weight: 600;
        font-size: 14px;
        line-height: 16px;
        min-height: 20px;
        color: #444444;
        margin-top: 16px;
    }
    ${injectCss}
`;

const PerPageSelect = styled.span<InjectableCss>`
    padding: 4px 16px;
    > div[class^="FormFieldStyle"] {
        border: 2px solid ${COLORS.background.grayShade};
        border-radius: 40px;
        background: ${COLORS.white} !important;
        width: 220px !important;
        padding: 0;
        label {
            font-size: ${TYPOGRAPHY.size.smaller} !important;
            font-weight: ${TYPOGRAPHY.weight.base} !important;
            line-height: ${TYPOGRAPHY.lineHeight.base} !important;
            color: ${getProp("theme.typography.p.color")} !important;
            padding-top: 7px !important;
        }
        > div[class^="FormInputWrapper"] {
            width: 100% !important;
            select {
                border: none !important;
                border-radius: 40px !important;
                font-size: ${TYPOGRAPHY.size.smaller} !important;
                font-weight: ${TYPOGRAPHY.weight.bold} !important;
                line-height: ${TYPOGRAPHY.lineHeight.base} !important;
                color: ${COLORS.black} !important;
                padding: 0px 7px;
            }
            span {
                color: ${getProp("theme.typography.p.color")} !important;
            }
        }
    }

    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    justify-content: center;
                    flex-basis: auto;
                `,
                css``,
                null,
                null,
                null,
            ],
            "max",
        )};

    ${injectCss}
`;

/**
 * Pagination is an interactive component that provides a navigation interface for paged content.
 */
const NISPagination: React.FC<PaginationProps> = withTheme(props => {
    const {
        createHref,
        currentPage: possibleStringCurrentPage,
        onChangeResultsPerPage,
        onChangePage,
        resultsPerPage: possibleStringResultsPerPage,
        resultsPerPageLabel,
        resultsCount,
        resultsPerPageOptions,
        pageSizeCookie,
        prevPage,
        nextPage,
        ...otherProps
    } = props;

    React.useEffect(() => {
        const { prevQueryString, currentQueryString, nextQueryString } = getPrevCurrentNextQueryString(
            prevPage,
            nextPage,
            convertToNumberIfString(possibleStringCurrentPage),
            "",
        );
        const prevCanonical = `${getCanonical()}${prevQueryString}`;
        const nextCanonical = `${getCanonical()}${nextQueryString}`;
        updateCanonical(currentQueryString);

        const { hasPrev, hasNext } = hasLinksWithRelPrevNext(prevCanonical, nextCanonical);
        const prevNotFromProducts = prevPage?.indexOf("/api/v2/products?") < 0 && prevPage?.indexOf("search=") < 0;
        const nextNotFromProducts = nextPage?.indexOf("/api/v2/products?") < 0 && nextPage?.indexOf("search=") < 0;

        if (prevPage && !hasPrev && prevNotFromProducts) {
            appendLinkElement("prev", prevCanonical);
        }

        if (nextPage && !hasNext && nextNotFromProducts) {
            appendLinkElement("next", nextCanonical);
        }

        return () => {
            const { hasPrev, hasNext } = hasLinksWithRelPrevNext(prevPage, nextPage);
            if (prevPage && hasPrev && prevNotFromProducts) {
                removeLinkElement("prev", prevCanonical);
            }

            if (nextPage && hasNext && nextNotFromProducts) {
                removeLinkElement("next", nextCanonical);
            }
        };
    }, [prevPage, nextPage]);

    const currentPage = convertToNumberIfString(possibleStringCurrentPage);
    const resultsPerPage = convertToNumberIfString(possibleStringResultsPerPage);
    const startIndex = resultsCount === 0 ? 0 : (currentPage - 1) * resultsPerPage + 1;
    const endIndex = Math.min(currentPage * resultsPerPage, resultsCount);

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.currentTarget.value;
        if (pageSizeCookie) {
            setCookie(pageSizeCookie, value);
        }

        onChangeResultsPerPage(event, parseInt(value, 10));
        event.currentTarget.blur();
    };

    const { spreadProps, applyProp } = applyPropBuilder(otherProps, { component: "pagination" });
    const cssOverrides = spreadProps("cssOverrides" as any);
    const buttonProps = spreadProps("buttonProps" as any);
    const currentPageButtonVariant = applyProp("currentPageButtonVariant");
    const translate = otherProps.theme.translate;
    const navIconsSrc = spreadProps("navIconsSrc");

    const finalPageIndex = Math.floor((resultsCount - 1) / resultsPerPage) + 1;
    let pagesToDisplay: ("e" | number)[] = [];
    if (finalPageIndex < 6) {
        for (let i = 1; i <= finalPageIndex; i += 1) {
            pagesToDisplay.push(i);
        }
    } else if (currentPage < 4) {
        pagesToDisplay = [1, 2, 3, 4, "e", finalPageIndex];
    } else if (currentPage >= 4 && currentPage < finalPageIndex - 2) {
        pagesToDisplay = [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, "e", finalPageIndex];
    } else {
        pagesToDisplay = [
            "e",
            finalPageIndex - 4,
            finalPageIndex - 3,
            finalPageIndex - 2,
            finalPageIndex - 1,
            finalPageIndex,
        ];
    }

    const paginationButtons = pagesToDisplay.map(page => {
        const getPageHref = (page: number | "e") => {
            if (typeof window !== "undefined" && window.location) {
                window?.scrollTo(0, 0);
                const searchParams = new URLSearchParams(window.location.search);
                searchParams.set("page", page.toString());
                const { pathname, search } = window.location;
                return search.length > 0 ? `${pathname}?${searchParams}` : `${pathname}?page=${page}`;
            }
            return "";
        };

        const href = getPageHref(page);
        if (page === "e") {
            return (
                <li key="ellipsis">
                    <StyledWrapper
                        as="span"
                        css={cssOverrides.ellipsis}
                        data-test-selector="pagination_ellipsis"
                        style={{
                            display: "inline-block",
                            marginRight: "8px",
                            fontFamily: "Roboto Condensed",
                            fontWeight: "600",
                            marginLeft: "0px",
                        }}
                    >
                        &hellip;
                    </StyledWrapper>
                </li>
            );
        }

        return (
            <li key={`page${page}`}>
                <Link
                    {...buttonDisplayProps(props, {
                        moreCss: numericButtonStyles,
                        page,
                        cssOverrides,
                        buttonProps,
                        currentPageButtonVariant,
                    })}
                    type="button"
                    href={href}
                    {...styles.paginatinLinkStyles}
                >
                    {page.toLocaleString("en-US")}
                </Link>
            </li>
        );
    });

    const forwardDisabled = currentPage === finalPageIndex || finalPageIndex === 1;
    const backDisabled = currentPage === 1 || finalPageIndex === 1;

    const navIcon = (
        pageIndex: number,
        icon: string | React.ComponentType,
        ariaLabel: string,
        disabledFlag: boolean,
        dataTestSelector?: string,
    ) => (
        <Button
            variant="secondary"
            {...buttonDisplayProps(props, {
                page: -1,
                moreCss: iconButtonStyles,
                cssOverrides,
                buttonProps,
                currentPageButtonVariant,
            })}
            aria-label={ariaLabel}
            disabled={disabledFlag}
            onClick={() => onChangePage(pageIndex)}
            {...(dataTestSelector && { "data-test-selector": dataTestSelector })}
        >
            {/* <VisuallyHidden>{ariaLabel}</VisuallyHidden> */}
            <ButtonIcon size={24} src={icon} />
        </Button>
    );

    return (
        <StyledWrapper {...styles.container} data-test-selector="Pagination_Background">
            <PaginationStyle css={cssOverrides.pagination} {...omitSingle(otherProps, "cssOverrides")}>
                <LinkList
                    role="navigation"
                    aria-label={translate("Pagination navigation")}
                    css={cssOverrides.linkList}
                    data-test-selector={`pagination_currentPage_${currentPage}`}
                >
                    <ul>
                        {/* <li>
                            {navIcon(
                                1,
                                navIconsSrc.firstPage,
                                translate("First page"),
                                backDisabled,
                                "pagination_firstButton",
                            )}
                        </li> */}
                        <li style={{ width: "24px", height: "24px", marginRight: "16px" }}>
                            {navIcon(
                                currentPage - 1,
                                navIconsSrc.previousPage,
                                translate("Previous page"),
                                backDisabled,
                                "pagination_previousButton",
                            )}
                        </li>
                        {paginationButtons}
                        <li style={{ width: "24px", height: "24px", marginLeft: "8px" }}>
                            {navIcon(
                                currentPage + 1,
                                navIconsSrc.nextPage,
                                translate("Next page"),
                                forwardDisabled,
                                "pagination_nextButton",
                            )}
                        </li>
                        {/* <li>
                            {navIcon(
                                finalPageIndex,
                                navIconsSrc.lastPage,
                                translate("Last page"),
                                forwardDisabled,
                                "pagination_lastButton",
                            )}
                        </li> */}
                    </ul>
                    <ul>
                        <li data-test-selector="itemCountLine">
                            {translate
                                ? `${translate("Showing")} ${startIndex}-${endIndex} ${translate("of")} ${resultsCount} ${translate("results")}`
                                : `Showing ${startIndex}-${endIndex} of ${resultsCount} results`}
                        </li>
                    </ul>
                </LinkList>

                {/* {resultsPerPageOptions?.length > 0 && (
                    <PerPageSelect css={cssOverrides.perPageSelect}>
                        <Select
                            onChange={changeResultsPerPage}
                            label={resultsPerPageLabel || translate("Results Per Page")}
                            labelPosition="left"
                            value={resultsPerPage}
                            {...spreadProps("selectProps" as any)}
                            data-test-selector="pagination_resultsPerPage"
                        >
                            {resultsPerPageOptions.map((n: string) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </Select>
                    </PerPageSelect>
                )} */}
            </PaginationStyle>
        </StyledWrapper>
    );
});

/** @component */
export default NISPagination as React.ComponentType<PaginationComponentProps>;

export { NISPagination, PaginationStyle };
